/**
 * The sharing panel.
 *
 * Every share names an existing account - there are no public links - so this
 * is a picker over the family, not a link generator. Only an owner sees it:
 * an editor can add to a shared folder but cannot pass access on.
 */
import { useState } from 'react';
import { Trash2, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { useGrantShare, usePeers, useRevokeShare, useShares } from '../api/hooks';
import { Button, Field, Modal, Select, Spinner, cx } from './ui';
import type { ShareSubjectType } from '../api/types';

const ROLES = [
  { value: 'viewer' as const, label: 'Can view' },
  { value: 'editor' as const, label: 'Can add and edit' },
];

export function ShareDialog({
  open,
  onOpenChange,
  subjectType,
  subjectId,
  subjectName,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subjectType: ShareSubjectType;
  subjectId: string;
  subjectName: string;
}) {
  const shares = useShares(open ? subjectType : null, open ? subjectId : null);
  const peers = usePeers();
  const grant = useGrantShare();
  const revoke = useRevokeShare();

  const [granteeId, setGranteeId] = useState('');
  const [role, setRole] = useState<'viewer' | 'editor'>('viewer');

  const alreadyShared = new Set(shares.data?.items.map((share) => share.grantee.id) ?? []);
  const available = (peers.data?.items ?? []).filter((peer) => !alreadyShared.has(peer.id));

  const onGrant = async () => {
    if (!granteeId) return;
    try {
      await grant.mutateAsync({ subjectType, subjectId, granteeId, role });
      setGranteeId('');
      toast.success('Shared');
    } catch (error) {
      toast.error('Could not share', { description: (error as Error).message });
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={`Share “${subjectName}”`}
      description="Only people with an account here can be given access."
      footer={<Button onClick={() => onOpenChange(false)}>Done</Button>}
    >
      {shares.isLoading || peers.isLoading ? (
        <Spinner />
      ) : (
        <>
          <div className="mb-5 rounded-xl border border-line bg-surface-2/60 p-3">
            <Field label="Give access to">
              {available.length === 0 ? (
                <p className="text-sm text-ink-dim">
                  {peers.data?.items.length === 0
                    ? 'There are no other accounts yet.'
                    : 'Everyone already has access.'}
                </p>
              ) : (
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Select
                    ariaLabel="Person"
                    className="flex-1"
                    value={granteeId}
                    onChange={setGranteeId}
                    options={[
                      { value: '', label: 'Choose a person…' },
                      ...available.map((peer) => ({ value: peer.id, label: peer.displayName })),
                    ]}
                  />
                  <Select
                    ariaLabel="Permission"
                    value={role}
                    onChange={setRole}
                    options={ROLES}
                  />
                  <Button
                    variant="primary"
                    disabled={!granteeId}
                    loading={grant.isPending}
                    onClick={onGrant}
                  >
                    <UserPlus className="size-4" aria-hidden />
                    Share
                  </Button>
                </div>
              )}
            </Field>
            <p className="text-xs text-ink-dim">
              {role === 'editor'
                ? 'Editors can upload into this and rename what is inside. Only you can delete it or share it further.'
                : 'Viewers can look at this and download it. Nothing else.'}
            </p>
          </div>

          <h3 className="mb-2 text-xs font-medium text-ink-dim">Who has access</h3>
          {shares.data?.items.length === 0 ? (
            <p className="rounded-lg border border-dashed border-line px-3 py-6 text-center text-sm text-ink-dim">
              Not shared with anyone yet.
            </p>
          ) : (
            <ul className="divide-y divide-line rounded-xl border border-line">
              {shares.data?.items.map((share) => (
                <li key={share.id} className="flex items-center gap-3 px-3 py-2.5">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{share.grantee.displayName}</p>
                    <p className="truncate text-xs text-ink-dim">@{share.grantee.username}</p>
                  </div>
                  <Select
                    ariaLabel={`Permission for ${share.grantee.displayName}`}
                    value={share.role}
                    onChange={(next) =>
                      grant.mutate({
                        subjectType,
                        subjectId,
                        granteeId: share.grantee.id,
                        role: next,
                      })
                    }
                    options={ROLES}
                    className={cx('shrink-0')}
                  />
                  <Button
                    icon
                    variant="ghost"
                    aria-label={`Remove ${share.grantee.displayName}`}
                    onClick={() => revoke.mutate(share.id)}
                  >
                    <Trash2 className="size-4 text-danger" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </Modal>
  );
}
